import { useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { DomainExpandedRow } from "./DomainExpandedRow";
import Pagination from "components/Pagination";
import Button from "components/Button";
import useIsMobile from "hooks/useIsMobile";
import { useAnalyticsFilters } from "providers/AnalyticsProvider/AnalyticsFilterProvider";
import { useOverviewData } from "providers/AnalyticsProvider/OverviewDataProvider";
import { useDomainData } from "providers/AnalyticsProvider/DomainDataProvider";
import { useAnalyticsDownload } from "hooks/analytics";
import FilterControls from "../shared/components/FilterControls";
import { DataTable, Column } from "components/Table";
import { TABLE_CONFIGS } from "utils/analytics/tableConfigs";
import EmptyTableState from "../shared/components/EmptyTableState";
import { mapDomainListToTableData } from "utils/analytics/mapping";
import { OVERVIEW_PAGE_SIZE } from "utils/analytics/constants";
import { DomainTableRow } from "utils/analytics/types";
import { trackAnalyticsActionClicked, trackAnalyticsSubpageViewed } from "utils/Tracking";

interface DomainAnalyticsTableProps {
  mode: "overview" | "full";
}

interface DomainDataTableProps {
  rows: DomainTableRow[];
  isLoading: boolean;
  error: boolean;
  onRetry: () => void;
}

const DomainDataTable = ({ rows, isLoading, error, onRetry }: DomainDataTableProps) => {
  const config = TABLE_CONFIGS.domain;
  const { selectedDomainColumns } = useAnalyticsFilters();
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  // Reset during render (child effects run first). Keyed on ids + isLoading: ids catch
  // cached page swaps, isLoading catches same-id refetches.
  const resetKey = `${isLoading}|${rows.map((row) => row.domain).join(",")}`;
  const [seenKey, setSeenKey] = useState(resetKey);
  if (resetKey !== seenKey) {
    setSeenKey(resetKey);
    setExpandedDomains(new Set());
  }

  const toggleExpanded = (domain: string) =>
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });

  const visibleHeaders = selectedDomainColumns?.length
    ? config.headers.filter((h) => selectedDomainColumns.includes(h.key))
    : config.headers;

  const columns: Column<DomainTableRow>[] = [
    {
      key: "expand",
      header: "",
      thClassName: "w-9 !px-2",
      cellClassName: "!px-2",
      cell: (row) => (
        <ChevronDownIcon
          className={`w-5 h-5 mx-auto transition-transform ${
            expandedDomains.has(row.domain) ? "rotate-180" : ""
          }`}
        />
      ),
    },
    ...visibleHeaders.map(
      (header): Column<DomainTableRow> => ({
        key: header.key,
        header: header.label,
        tooltip: header.tooltip,
        cell: (row) =>
          header.key === "domain" ? (
            <div className="break-all max-w-full pr-4 overflow-hidden">{row.domain || "N/A"}</div>
          ) : (
            (row as any)[header.key] ?? "N/A"
          ),
      })
    ),
  ];

  return (
    <DataTable
      columns={columns}
      rows={error ? [] : rows}
      getRowKey={(row) => row.domain}
      isLoading={isLoading}
      onRowClick={(row) => toggleExpanded(row.domain)}
      className="min-w-[900px]"
      empty={<EmptyTableState type="domain" error={error} onRetry={onRetry} />}
      renderExpandedRow={(row) =>
        expandedDomains.has(row.domain) ? <DomainExpandedRow item={row} /> : null
      }
    />
  );
};

const OverviewDomainTable = () => {
  const navigate = useNavigate();
  const config = TABLE_CONFIGS.domain;
  const { overviewDomainTableData, isOverviewLoading, error, refetchOverview } = useOverviewData();

  const handleViewAll = useCallback(() => {
    trackAnalyticsSubpageViewed({ section: "domain_analytics" });
    navigate(config.route);
  }, [navigate, config.route]);

  return (
    <div className="flex gap-2 flex-col">
      <DomainDataTable
        rows={overviewDomainTableData}
        isLoading={isOverviewLoading}
        error={!!error}
        onRetry={refetchOverview}
      />
      {!error && overviewDomainTableData.length >= OVERVIEW_PAGE_SIZE && (
        <div className="mt-4">
          <Button
            fullWidth
            text={config.viewAllButtonText}
            onClick={handleViewAll}
            className="button button-secondary"
          />
        </div>
      )}
    </div>
  );
};

const FullDomainTable = () => {
  const config = TABLE_CONFIGS.domain;
  const { dateRange, filters } = useAnalyticsFilters();
  const { domainData, goToDomainPage, hasDomainNextPage, isDomainPageLoading, domainPageError, retryDomainPage } = useDomainData();
  const { download, isDownloading } = useAnalyticsDownload();

  const isMobile = useIsMobile();
  const isTablet = useMediaQuery({
    query: "(min-width: 640px) and (max-width: 1024px)",
  });

  const data = useMemo(
    () => mapDomainListToTableData(domainData.list),
    [domainData.list]
  );

  const handleDownload = useCallback(() => {
    download({
      dateRange,
      filters,
      filenamePrefix: "domain-analytics",
      type: "domains",
      trackingEvent: "domain_analytics",
    });
  }, [dateRange, filters, download]);

  return (
    <>
      <FilterControls
        tableHeaders={config.headers}
        downloadButtonOnClick={handleDownload}
        downloadButtonText={isMobile ? "" : "DOWNLOAD REPORT"}
        downloadButtonDisabled={isDownloading}
        downloadButtonLoading={isDownloading}
        downloadButtonLoadingText="DOWNLOADING..."
        isMobile={isMobile}
        isTablet={isTablet}
      />
      <div className="flex gap-2 flex-col">
        <DomainDataTable
          rows={data}
          isLoading={isDomainPageLoading}
          error={!!domainPageError}
          onRetry={retryDomainPage}
        />
        {/* Past page 1, pagination stays visible even on error so cached
            earlier pages remain reachable */}
        {(domainData.currentPage > 1 || (hasDomainNextPage && !domainPageError)) && (
          <Pagination
            currentPage={domainData.currentPage}
            onPageChange={goToDomainPage}
            hasNextPage={hasDomainNextPage}
            onTrack={(action) =>
              trackAnalyticsActionClicked({ section: "global_all", action, target: "overview_tables" })
            }
          />
        )}
      </div>
    </>
  );
};

const DomainAnalyticsTable = ({ mode }: DomainAnalyticsTableProps) => {
  if (mode === "overview") return <OverviewDomainTable />;
  return <FullDomainTable />;
};

export default DomainAnalyticsTable;
